import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { MemberVerificationLabelItemComponent } from './member-verification-label-item/member-verification-label-item.component';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StringExtensions } from '../../../../utils/string.extensions';

interface VerificationMember {
  firstName: string;
  lastName: string;
  socialInsuranceNumber: string;
  birthDate: string;
}

interface PotentialMatchedMember {
  memberId: number;
  firstName: string;
  lastName: string;
  socialInsuranceNumber: string;
  birthDate: string;
}

@Component({
  selector: 'app-member-verification-details',
  templateUrl: './member-verification-details.component.html',
  styleUrls: ['./member-verification-details.component.scss'],
  standalone: true,
  imports: [AsyncPipe, MemberVerificationLabelItemComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MemberVerificationDetailsComponent implements OnChanges {
  @Input() submittedMember!: VerificationMember;
  @Input() matchedMember!: PotentialMatchedMember;
  @Input() isSubmittedMember: boolean = false;
  @Input() isLoading: boolean = false;

  private _submittedMember = new BehaviorSubject<VerificationMember | null>(null);
  public submittedMember$ = this._submittedMember as Observable<VerificationMember | null>;

  private _matchedMember = new BehaviorSubject<PotentialMatchedMember | null>(null);
  public matchedMember$ = this._matchedMember as Observable<PotentialMatchedMember | null>;

  private _isSubmittedMember = new BehaviorSubject<boolean>(false);
  public isSubmittedMember$ = this._isSubmittedMember as Observable<boolean>;

  private _isLoading = new BehaviorSubject<boolean>(false);
  public isLoading$ = this._isLoading as Observable<boolean>;

  protected highlightFirstName = false;
  protected highlightLastName = false;
  protected highlightSin = false;
  protected highlightBirthDate = false;

  public firstName$ = combineLatest([this.isSubmittedMember$, this.submittedMember$, this.matchedMember$]).pipe(
    map(([isSubmittedMember, submittedMember, matchedMember]) => {
      this.highlightFirstName =
        !isSubmittedMember && submittedMember?.firstName?.toLowerCase() === matchedMember?.firstName?.toLowerCase();
      return isSubmittedMember ? submittedMember?.firstName : matchedMember?.firstName;
    })
  );

  public lastName$ = combineLatest([this.isSubmittedMember$, this.submittedMember$, this.matchedMember$]).pipe(
    map(([isSubmittedMember, submittedMember, matchedMember]) => {
      this.highlightLastName =
        !isSubmittedMember && submittedMember?.lastName?.toLowerCase() === matchedMember?.lastName?.toLowerCase();
      return isSubmittedMember ? submittedMember?.lastName : matchedMember?.lastName;
    })
  );

  public sinNumber$ = combineLatest([this.isSubmittedMember$, this.submittedMember$, this.matchedMember$]).pipe(
    map(([isSubmittedMember, submittedMember, matchedMember]) => {
      this.highlightSin =
        !isSubmittedMember && submittedMember?.socialInsuranceNumber === matchedMember?.socialInsuranceNumber;
      const sin = isSubmittedMember ? submittedMember?.socialInsuranceNumber : matchedMember?.socialInsuranceNumber;
      return StringExtensions.addSpacesToSin(sin ?? '');
    })
  );

  public birthDate$ = combineLatest([this.isSubmittedMember$, this.submittedMember$, this.matchedMember$]).pipe(
    map(([isSubmittedMember, submittedMember, matchedMember]) => {
      this.highlightBirthDate = !isSubmittedMember && submittedMember?.birthDate === matchedMember?.birthDate;
      const birthday = isSubmittedMember ? submittedMember?.birthDate : matchedMember?.birthDate;
      return StringExtensions.convertDateStringToReadableDate(birthday ?? '');
    })
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.submittedMember) this._submittedMember.next(this.submittedMember);
    if (changes.matchedMember) this._matchedMember.next(this.matchedMember);
    if (changes.isSubmittedMember) this._isSubmittedMember.next(this.isSubmittedMember);
    if (changes.isLoading) this._isLoading.next(this.isLoading);
  }
}
